import { useEffect, useRef, useState } from 'react';
import { Popover } from 'bootstrap';
import { generatePopoverContent } from '../../../util/util';
import { useSelector } from 'react-redux';

const useInitializePopovers = () => {
    const { user } = useSelector((state: any) => state.commonSlice);

    const [notifications, setNotifications] = useState<{
            NOTIFICATION: string,
            REDIRECTURL: string,
            OBJTYPE: string,
            NOTIFICATIONAGE: string,
            OBJID: number
        }[]
    >([]);

    const popoverRef = useRef<Popover | null>(null);    // Global tracker for the currently open popover to open/close the same instance of popover

    useEffect(() => {
        setNotifications(user?.userNotifications || []);
    }, [user?.userNotifications]);

    useEffect(() => {
        // Check that particular popover exists
        const popoverTriggerElement = document.querySelector('[data-notification-popover="true"]');
        if (!popoverTriggerElement) return;
        // Dispose previous instance before creating a new one
        if (popoverRef.current) {
            popoverRef.current.dispose();
            popoverRef.current = null;
        }

        try {
            // We handle the popover manually
            const popover = new Popover(popoverTriggerElement, {
                html: true,
                sanitize: false,
                trigger: 'manual',
                content: generatePopoverContent(notifications)
            });

            popoverRef.current = popover;

            const handlePopoverShown = () => {
                const closeBtn = document.getElementById('popover-close-btn');

                if (closeBtn) {
                    // Remove any existing click listeners to prevent duplicates
                    closeBtn.onclick = null;
                    
                    // Add click event listener
                    const handleCloseClick = (e: MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        popover.hide();
                    };
                    
                    closeBtn.addEventListener('click', handleCloseClick, { once: false });
                }
            };

            const handleToggle = (e: Event) => {
                e.stopPropagation();
                const isShown = popoverTriggerElement.getAttribute('aria-describedby');

                if (isShown) {
                    popover.hide();
                } else {
                    popover.show();
                }
            };

            // Close popover on clicking X button
            popoverTriggerElement.addEventListener('shown.bs.popover', handlePopoverShown as EventListener);
            popoverTriggerElement.addEventListener('click', handleToggle);

            return () => {
                popoverTriggerElement.removeEventListener(
                    'shown.bs.popover',
                    handlePopoverShown as EventListener
                );

                popoverTriggerElement.removeEventListener('click', handleToggle);

                if (popoverRef.current) {
                    popoverRef.current.dispose();
                    popoverRef.current = null;
                }
            };
        } catch (error) {
            console.error('Error initializing popover:', error);
        }
    }, [notifications]);
};

export default useInitializePopovers;